import React from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import {
  SALES_PLAYBOOK_STATUS,
  SALES_PLAYBOOK_VARIANT_CLASS,
  SALES_TALK_TRACKS_LABEL,
  SALES_TALK_TRACKS_ROUTE,
} from 'components/AiCompetitiveIntelligence/constants';
import { SalesPlaybookCategory } from 'types/recommendations';

const Column = styled.div`
  flex: 1;
  min-width: 0;
  padding-left: 24px;
`;

const ColumnHeader = styled.div`
  display: flex;
  flex-flow: row;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 10px;
  padding-top: 20px;
  border-bottom: 1px solid var(--line-gray);
`;

const ColumnTitle = styled.div`
  font-family: var(--font-bold);
  font-size: 14px;
`;

const ColumnLink = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--dark-blue);
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
`;

const EmptyMessage = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
  padding: 16px 0;
`;

const TracksList = styled.div`
  padding-bottom: 30px;
`;

const TrackRow = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 10px;
  padding: 10px 0;
  border-bottom: solid 1px var(--line-gray);
`;

const TrackLabelGroup = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 8px;
`;

const TrackAccent = styled.div`
  width: 4px;
  height: 19px;
  border-radius: 4px;

  &.lead-with {
    background-image: linear-gradient(to bottom, var(--mint-green), var(--positive-green));
  }
  &.position-against {
    background-image: linear-gradient(to bottom, var(--lavender-purple), var(--competitor-purple));
  }
  &.handle-objections {
    background-image: linear-gradient(to bottom, var(--amber-light), var(--inactive-orange));
  }
`;

const TrackLabel = styled.div`
  font-size: 14px;
  white-space: nowrap;
`;

const TrackItemsText = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  text-align: right;
  display: flex;
  flex-flow: column;
  gap: 4px;
`;

const SALES_TALK_TRACK_BUCKETS = [
  { status: SALES_PLAYBOOK_STATUS.LEAD_WITH, label: 'Lead With' },
  { status: SALES_PLAYBOOK_STATUS.POSITION_AGAINST, label: 'Position Against' },
  { status: SALES_PLAYBOOK_STATUS.HANDLE_OBJECTIONS, label: 'Objections to Handle' },
];

type Props = {
  categories: SalesPlaybookCategory[];
  totalCount: number;
};

const SalesTalkTracksColumn = ({ categories, totalCount }: Props) => {
  const navigate = useNavigate();

  const goTo = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(path);
  };

  return (
    <Column data-test-id="activity-summary-sales-talk-tracks-column">
      <ColumnHeader>
        <ColumnTitle>{SALES_TALK_TRACKS_LABEL}</ColumnTitle>
        <ColumnLink
          data-test-id="activity-summary-sales-talk-tracks-link"
          href={SALES_TALK_TRACKS_ROUTE}
          onClick={goTo(SALES_TALK_TRACKS_ROUTE)}
        >
          All Tracks ({totalCount}) <img src="/icons/chevron-right-blue.svg" width="16" height="16" alt="" />
        </ColumnLink>
      </ColumnHeader>
      <TracksList>
        {categories.length === 0 && (
          <EmptyMessage data-test-id="activity-summary-sales-talk-tracks-empty">No sales talk tracks yet.</EmptyMessage>
        )}
        {categories.length > 0 &&
          SALES_TALK_TRACK_BUCKETS.map((bucket) => {
            const items = categories.filter((category) => category.status === bucket.status);
            if (items.length === 0) return null;

            return (
              <TrackRow key={bucket.status} data-test-id={`activity-summary-sales-talk-track-${bucket.status}`}>
                <TrackLabelGroup>
                  <TrackAccent className={SALES_PLAYBOOK_VARIANT_CLASS[bucket.status]} />
                  <TrackLabel>{bucket.label}</TrackLabel>
                </TrackLabelGroup>
                <TrackItemsText>
                  {items.map((item, index) => (
                    <div key={index}>{item.title}</div>
                  ))}
                </TrackItemsText>
              </TrackRow>
            );
          })}
      </TracksList>
    </Column>
  );
};

export default SalesTalkTracksColumn;
